import qs from 'qs'
import moment from 'moment'
import {
  get as getLocalStorage,
  set as setLocalStorage,
} from 'lib/storage/isomorphicLocalStorage'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import {
  markCarHireSearchRefinementsSkipped,
} from './carHireSearchRefinements'

const CAR_HIRE_RECENT_SEARCHES_KEY = 'carHireRecentSearches'
const CAR_HIRE_RECENT_SEARCHES_LIMIT = 3

export interface RecentCarHireSearch {
  id: string
  searchedOn: number
  pickUpName: string
  dropOffName: string
  pickUpId: string
  dropOffId: string
  pickUpDate: string
  pickUpTime: string
  dropOffDate: string
  dropOffTime: string
  driversAgeCategory: App.CarHireDriverAgeCategory
  driversAge?: number
  isPickUpAtAirport?: boolean
  pickUpSearchType?: App.CarHireSearchTypes
  dropOffSearchType?: App.CarHireSearchTypes
  pickUpLatitude?: number
  pickUpLongitude?: number
  dropOffLatitude?: number
  dropOffLongitude?: number
}

type RecentCarHireSearchInput = Omit<RecentCarHireSearch, 'id' | 'searchedOn'>

interface RecentCarHireSearchNavigation {
  query: string
  url: string
}

function getSearchParams(search: string | URLSearchParams) {
  return search instanceof URLSearchParams ? search : new URLSearchParams(search)
}

function getStringParam(searchParams: URLSearchParams, key: string) {
  return searchParams.get(key) || undefined
}

function getNumberParam(searchParams: URLSearchParams, key: string) {
  const value = searchParams.get(key)
  if (!value) {
    return
  }

  const parsedValue = Number(value)
  return Number.isFinite(parsedValue) ? parsedValue : undefined
}

function getBooleanParam(searchParams: URLSearchParams, key: string) {
  const value = searchParams.get(key)
  if (value === 'true') return true
  if (value === 'false') return false
}

function isCarHireDriverAgeCategory(value: number | undefined): value is App.CarHireDriverAgeCategory {
  return value === 1 || value === 2 || value === 3
}

function recentSearchKey(search: RecentCarHireSearchInput | RecentCarHireSearch) {
  return JSON.stringify([
    search.pickUpName,
    search.dropOffName,
    search.pickUpId,
    search.dropOffId,
    search.pickUpDate,
    search.pickUpTime,
    search.dropOffDate,
    search.dropOffTime,
    search.driversAgeCategory,
    search.driversAge,
    search.isPickUpAtAirport,
    search.pickUpSearchType,
    search.dropOffSearchType,
    search.pickUpLatitude,
    search.pickUpLongitude,
    search.dropOffLatitude,
    search.dropOffLongitude,
  ])
}

function isRecentCarHireSearch(search: unknown): search is RecentCarHireSearch {
  if (!search || typeof search !== 'object') {
    return false
  }

  const candidate = search as RecentCarHireSearch
  return typeof candidate.id === 'string' &&
    typeof candidate.searchedOn === 'number' &&
    typeof candidate.pickUpName === 'string' &&
    typeof candidate.dropOffName === 'string' &&
    typeof candidate.pickUpId === 'string' &&
    typeof candidate.dropOffId === 'string' &&
    typeof candidate.pickUpDate === 'string' &&
    typeof candidate.pickUpTime === 'string' &&
    typeof candidate.dropOffDate === 'string' &&
    typeof candidate.dropOffTime === 'string' &&
    isCarHireDriverAgeCategory(candidate.driversAgeCategory)
}

function isCurrentOrFutureDate(date: string) {
  const parsedDate = moment(date, ISO_DATE_FORMAT, true)
  return parsedDate.isValid() && parsedDate.isSameOrAfter(moment(), 'day')
}

function isCurrentOrFutureRecentCarHireSearch(search: RecentCarHireSearchInput | RecentCarHireSearch) {
  return isCurrentOrFutureDate(search.pickUpDate) && isCurrentOrFutureDate(search.dropOffDate)
}

export function getLocalRecentCarHireSearches(): Array<RecentCarHireSearch> {
  const recentSearches = getLocalStorage(CAR_HIRE_RECENT_SEARCHES_KEY)
  if (!Array.isArray(recentSearches)) {
    return []
  }

  return recentSearches.filter(isRecentCarHireSearch).filter(isCurrentOrFutureRecentCarHireSearch)
}

function setLocalRecentCarHireSearches(searches: Array<RecentCarHireSearch>) {
  try {
    setLocalStorage(CAR_HIRE_RECENT_SEARCHES_KEY, searches)
  } catch {
    // Recent searches are best-effort only; never block the actual search journey.
  }
}

export function buildRecentCarHireSearchFromQuery(search: string | URLSearchParams): RecentCarHireSearchInput | undefined {
  const searchParams = getSearchParams(search)
  const driversAgeCategory = getNumberParam(searchParams, 'ageCategory')

  if (!isCarHireDriverAgeCategory(driversAgeCategory)) {
    return
  }

  const pickUpName = getStringParam(searchParams, 'pickUpName')
  const dropOffName = getStringParam(searchParams, 'dropOffName')
  const pickUpId = getStringParam(searchParams, 'pickUpId')
  const dropOffId = getStringParam(searchParams, 'dropOffId')
  const pickUpDate = getStringParam(searchParams, 'pickUpDate')
  const pickUpTime = getStringParam(searchParams, 'pickUpTime')
  const dropOffDate = getStringParam(searchParams, 'dropOffDate')
  const dropOffTime = getStringParam(searchParams, 'dropOffTime')

  if (!pickUpName || !dropOffName || !pickUpId || !dropOffId || !pickUpDate || !pickUpTime || !dropOffDate || !dropOffTime) {
    return
  }

  return {
    pickUpName,
    dropOffName,
    pickUpId,
    dropOffId,
    pickUpDate,
    pickUpTime,
    dropOffDate,
    dropOffTime,
    driversAgeCategory,
    ...((driversAgeCategory === 1 || driversAgeCategory === 3) && { driversAge: getNumberParam(searchParams, 'age') }),
    isPickUpAtAirport: getBooleanParam(searchParams, 'isPickUpAtAirport'),
    pickUpSearchType: getStringParam(searchParams, 'pickUpSearchType') as App.CarHireSearchTypes | undefined,
    dropOffSearchType: getStringParam(searchParams, 'dropOffSearchType') as App.CarHireSearchTypes | undefined,
    pickUpLatitude: getNumberParam(searchParams, 'pickUpLatitude'),
    pickUpLongitude: getNumberParam(searchParams, 'pickUpLongitude'),
    dropOffLatitude: getNumberParam(searchParams, 'dropOffLatitude'),
    dropOffLongitude: getNumberParam(searchParams, 'dropOffLongitude'),
  }
}

export function saveRecentCarHireSearch(search: RecentCarHireSearchInput) {
  if (!isCurrentOrFutureRecentCarHireSearch(search)) {
    return
  }

  const nextSearch = {
    ...search,
    id: recentSearchKey(search),
    searchedOn: Date.now(),
  }
  const recentSearches = getLocalRecentCarHireSearches()
  const seenKeys = new Set<string>()
  const nextSearches = [nextSearch, ...recentSearches]
    .filter((recentSearch) => {
      const key = recentSearchKey(recentSearch)
      if (seenKeys.has(key)) {
        return false
      }
      seenKeys.add(key)
      return true
    })
    .slice(0, CAR_HIRE_RECENT_SEARCHES_LIMIT)

  setLocalRecentCarHireSearches(nextSearches)
}

export function saveRecentCarHireSearchFromQuery(search: string | URLSearchParams) {
  const recentSearch = buildRecentCarHireSearchFromQuery(search)
  if (recentSearch) {
    saveRecentCarHireSearch(recentSearch)
  }
}

export function bumpRecentCarHireSearch(search: RecentCarHireSearch) {
  const currentSearches = getLocalRecentCarHireSearches()
  const key = recentSearchKey(search)
  const nextSearches = [
    search,
    ...currentSearches.filter(recentSearch => recentSearchKey(recentSearch) !== key),
  ].slice(0, CAR_HIRE_RECENT_SEARCHES_LIMIT)

  setLocalRecentCarHireSearches(nextSearches)
}

export function buildCarHireRecentSearchBaseQuery(search: RecentCarHireSearch) {
  return qs.stringify({
    pickUpName: search.pickUpName,
    dropOffName: search.dropOffName,
    pickUpId: search.pickUpId,
    dropOffId: search.dropOffId,
    pickUpDate: search.pickUpDate,
    pickUpTime: search.pickUpTime,
    dropOffDate: search.dropOffDate,
    dropOffTime: search.dropOffTime,
    ageCategory: search.driversAgeCategory,
    age: (search.driversAgeCategory === 1 || search.driversAgeCategory === 3) ? search.driversAge : undefined,
    isPickUpAtAirport: search.isPickUpAtAirport,
    pickUpSearchType: search.pickUpSearchType,
    dropOffSearchType: search.dropOffSearchType,
    pickUpLatitude: search.pickUpLatitude,
    pickUpLongitude: search.pickUpLongitude,
    dropOffLatitude: search.dropOffLatitude,
    dropOffLongitude: search.dropOffLongitude,
  })
}

export function getRecentCarHireSearchNavigation(search: RecentCarHireSearch): RecentCarHireSearchNavigation {
  const baseQuery = buildCarHireRecentSearchBaseQuery(search)

  return {
    query: baseQuery,
    url: `/search/car-hire?${baseQuery}`,
  }
}

export function markRecentCarHireSearchNavigation(search: RecentCarHireSearchNavigation) {
  markCarHireSearchRefinementsSkipped(search.query)
}
